<template>
  <div>
    <title-bar :title-stack="titleStack" />
    <section class="section is-main-section">
      <b-loading :active="isLoading" :is-full-page="false" />
      <div v-if="notFound" class="notification is-warning">
        Dosarul nu exista.
        <router-link :to="{ name: 'succession.files' }"
          >Inapoi la lista</router-link
        >
      </div>
      <div v-else-if="notAllowed" class="notification is-warning">
        Operatia nu este permisa in starea actuala a dosarului.
        <router-link
          :to="{ name: 'succession.file', params: { id: $route.params.id } }"
        >
          Inapoi la dosar
        </router-link>
      </div>
      <template v-else-if="initial">
        <b-notification v-if="staleMessage" type="is-warning" :closable="false">
          {{ staleMessage }} Modificarile nesalvate se pierd la reincarcare.
          <b-button size="is-small" class="ml-2" @click="reload"
            >Reincarca dosarul</b-button
          >
        </b-notification>
        <b-notification v-if="duplicate" type="is-danger" :closable="false">
          Exista deja un dosar inregistrat pentru acest CNP:
          <router-link
            :to="{
              name: 'succession.file',
              params: { id: duplicate.successionFileId },
            }"
          >
            {{ duplicate.registration }}
          </router-link>
        </b-notification>
        <card-component
          :title="title"
          icon="file-document-edit"
          has-button-slot
        >
          <template #button>
            <div class="card-header-icon">
              <succession-help size="is-small" />
            </div>
          </template>
          <succession-file-fields
            ref="form"
            :key="formKey"
            :initial="initial"
            :mode="mode"
            :linked-number="detail ? detail.generalRegistryNumber : null"
            :linked-date="detail ? detail.generalRegistryDate : null"
            :show-chamber="showChamber"
            :chamber-required="chamberRequired"
            :chambers="chambers"
            :saving="saving"
            :today="today"
            @submit="onSubmit"
            @cancel="goBack"
          />
        </card-component>
      </template>
    </section>
  </div>
</template>

<script>
import SuccessionService from "@/services/succession.service";
import { notify } from "@/helpers/notify";
import {
  describeError,
  isDuplicateCnp,
  isReloadable,
} from "@/helpers/resdpsErrors";
import {
  allowedActions,
  emptyForm,
  formFromDetail,
  startOfDay,
} from "@/helpers/resdps";
import TitleBar from "@/components/TitleBar";
import CardComponent from "@/components/CardComponent";
import SuccessionFileFields from "@/components/succession/succession-file-form.vue";
import SuccessionHelp from "@/components/succession/succession-help.vue";

const MODES = {
  "succession.add": "add",
  "succession.edit": "edit",
  "succession.rectify": "rectify",
};
const REQUIRED_ACTIONS = { edit: "EditDraft", rectify: "Rectify" };
const SAVED_MESSAGES = {
  add: "Ciorna a fost creata.",
  edit: "Ciorna a fost salvata.",
  rectify: "Rectificarea a fost salvata.",
};

export default {
  name: "SuccessionFileForm",
  components: { TitleBar, CardComponent, SuccessionFileFields, SuccessionHelp },
  data() {
    return {
      detail: null,
      initial: null,
      isLoading: false,
      notFound: false,
      notAllowed: false,
      saving: false,
      staleMessage: null,
      duplicate: null,
      chambers: [],
      formKey: 0,
      leaving: false,
      today: startOfDay(new Date()),
    };
  },
  computed: {
    mode() {
      return MODES[this.$route.name] || "add";
    },
    showChamber() {
      if (this.mode !== "rectify" || !this.detail) return false;
      const hasChamber =
        this.detail.notaryChamberId !== null &&
        this.detail.notaryChamberId !== undefined;
      return this.detail.status === "Transmitted" || hasChamber;
    },
    chamberRequired() {
      return this.showChamber && this.detail.status === "Transmitted";
    },
    title() {
      if (this.mode === "add") return "Dosar succesoral nou";
      if (this.mode === "edit") return "Editare ciorna";
      return (
        "Rectificare dosar " +
        (this.detail && this.detail.registration
          ? this.detail.registration
          : "")
      );
    },
    titleStack() {
      return [{ label: "Succesiuni", to: "/succesiuni" }, this.title];
    },
  },
  created() {
    this.init();
  },
  beforeRouteLeave(to, from, next) {
    const form = this.$refs.form;
    if (this.leaving || !form || !form.isDirty) {
      next();
      return;
    }
    this.$buefy.dialog.confirm({
      title: "Modificari nesalvate",
      message: "Parasiti pagina fara sa salvati modificarile?",
      confirmText: "Paraseste pagina",
      cancelText: "Raman in formular",
      type: "is-warning",
      hasIcon: true,
      onConfirm: () => next(),
      onCancel: () => next(false),
    });
  },
  methods: {
    async init() {
      if (this.mode === "add") {
        this.initial = emptyForm();
        return;
      }
      // Dosarul venit din fisa nu se mai citeste (fiecare citire scrie ViewedFile in jurnal).
      const passed = this.$route.params.detail;
      const detail =
        passed && String(passed.id) === String(this.$route.params.id)
          ? passed
          : await this.loadDetail();
      if (detail) await this.useDetail(detail);
    },
    async useDetail(detail) {
      this.detail = detail;
      if (
        allowedActions(detail.status).indexOf(REQUIRED_ACTIONS[this.mode]) ===
        -1
      ) {
        this.notAllowed = true;
        this.initial = null;
        return;
      }
      this.notAllowed = false;
      this.initial = formFromDetail(detail);
      if (this.showChamber && this.chambers.length === 0)
        await this.loadChambers();
    },
    async loadDetail() {
      this.isLoading = true;
      try {
        return await SuccessionService.getFile(this.$route.params.id);
      } catch (error) {
        const described = describeError(error);
        if (described.status === 404) this.notFound = true;
        else notify.error(this, described.message);
        return null;
      } finally {
        this.isLoading = false;
      }
    },
    async loadChambers() {
      try {
        this.chambers = await SuccessionService.listNotaryChambers();
      } catch (error) {
        notify.error(this, describeError(error).message);
      }
    },
    async reload() {
      this.staleMessage = null;
      this.duplicate = null;
      const detail = await this.loadDetail();
      if (!detail) return;
      await this.useDetail(detail);
      this.formKey += 1;
    },
    save(data, reason) {
      if (this.mode === "add") return SuccessionService.createFile(data);
      if (this.mode === "edit")
        return SuccessionService.updateFile(
          this.detail.id,
          data,
          this.detail.rowVersion
        );
      return SuccessionService.rectifyFile(
        this.detail.id,
        data,
        reason,
        this.detail.rowVersion
      );
    },
    async onSubmit({ data, reason }) {
      this.saving = true;
      this.duplicate = null;
      try {
        const saved = await this.save(data, reason);
        // O rectificare fara schimbari intoarce dosarul nesalvat: rowVersion ramane acelasi.
        if (
          this.mode === "rectify" &&
          saved.rowVersion === this.detail.rowVersion
        ) {
          notify.error(this, "Nu ati modificat niciun camp.");
          return;
        }
        this.leaving = true;
        notify.success(this, SAVED_MESSAGES[this.mode]);
        this.$router.push({
          name: "succession.file",
          params: { id: saved.id, detail: saved },
        });
      } catch (error) {
        this.handleError(error);
      } finally {
        this.saving = false;
      }
    },
    handleError(error) {
      const described = describeError(error);
      notify.error(this, described.message);
      if (described.fields.length > 0 && this.$refs.form) {
        this.$refs.form.setApiErrors(described.fields, described.message);
      }
      if (isDuplicateCnp(described)) {
        this.duplicate = {
          successionFileId: described.data.successionFileId,
          registration: described.data.registration,
        };
      }
      if (isReloadable(described)) this.staleMessage = described.message;
    },
    goBack() {
      if (this.mode === "add") {
        this.$router.push({ name: "succession.files" });
        return;
      }
      this.$router.push({
        name: "succession.file",
        params: { id: this.detail.id, detail: this.detail },
      });
    },
  },
};
</script>
